import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { createSearchParams, useNavigate } from 'react-router-dom';

import {
	setDynamicListExporting,
	updateDynamicListData,
	updateDynamicListsInPage
} from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';

import { addShipmentResult } from 'pages/Shipments/ActionForms/CreateShipment/shipmentResultsSlice';
import { getNotificationCount, increaseNotificationCount } from 'pages/Topbar/Notifications/notificationsSlice';

import { PROFILE, STORAGE_PUBLIC } from 'services/consts';
import { fetchToken } from 'services/firebase';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

const NOTIFICATION_TYPES = {
	approveSupportAccess: 'supportAccessRequestApproved',
	dataExport: 'dataExport',
	importOrder: 'importOrder', // comes as "dataImport" success or valid data at second phase while server process the file
	importNoSuccess: 'importNoSuccess', // comes as "dataImport" failed at first phase while server process the file
	customerReview: 'customerReview',
	lowStock: 'lowStock',
	orderNeedConfirmation: 'orderNeedConfirmation',
	deliveredOrder: 'deliveredOrder',
	announcement: 'announcement',
	newOrder: 'newOrder',
	agedOrder: 'agedOrder',
	orderSLABreach: 'orderSLABreach',
	shipmentCreated: 'shipmentCreated',
	returnWaitingConfirmation: 'returnWaitingConfirmation',
	returnRequestReceived: 'returnRequestReceived',
	shipmentFailed: 'shipmentFailed',
	orderAssigned: 'orderAssigned',
	refereeSignedUp: 'refereeSignedUp',
	refereeRewarded: 'refereeRewarded',
	refereeCompletedShipments: 'refereeCompletedShipments',
	weightDeductionNote: 'weightDeductionNote',
	DEFAULT: 'DEFAULT'
};

const IMPORT_PATHS = {
	importExport: PATHS.IMPORT_LOGS,
	product: mergePaths(PATHS.PRODUCTS_MAIN, PATHS.PRODUCTS),
	order: mergePaths(PATHS.SHIPMENTS, PATHS.PENDING_ORDERS),
	warehouse: mergePaths(PATHS.WAREHOUSES),
	branch: mergePaths(PATHS.BRANCHES),
	user: mergePaths(PATHS.USERS),
	inventory: mergePaths(PATHS.INVENTORY_MAIN, PATHS.INVENTORY),
	invoice: PATHS.IMPORT_LOGS,
	binInventory: mergePaths(PATHS.INVENTORY_MAIN, PATHS.BIN_INVENTORY)
};

const audio = new Audio(`${STORAGE_PUBLIC}/sounds/notification-alert.mp3`);

const FirebaseNotifications = () => {
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();
	const { showInfo, showError, showSuccess, showWarning } = useAlert();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { getLocaleDateWithTime } = useLocale();

	const isUserInitialized = useSelector((state) => state.user?.initialized);

	useEffect(() => {
		if (isUserInitialized) {
			dispatch(getNotificationCount());
			fetchToken().then((token) => {
				axiosPrivate
					.post(PROFILE.SEND_FCM_TOKEN, {
						token
					})
					.catch((error) => {
						AppLogger.error(error);
						showError();
					});
			});
		}
	}, [isUserInitialized]);

	const handleDownloadTemplate = useCallback(
		async (name, link, date, translationObject) => {
			const blob = await (await fetch(link)).blob();
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = `${t(name, translationObject)}_${t('export')}_${getLocaleDateWithTime(date)}.xlsx`
				.split(' ')
				.join('_');
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		},
		[getLocaleDateWithTime, t]
	);

	const playAudio = () =>
		setInterval(() => {
			audio.play();
		}, 2000);

	const stopAudio = (id) => {
		clearInterval(id);
	};

	const NOTIFICATION_ACTIONS = useMemo(
		() => ({
			// Import order action notification for success, failed and partial success
			[NOTIFICATION_TYPES.importOrder]: (payload) => {
				if (!payload?.notification?.title) {
					AppLogger.error('Import Order Notification Missing Title: ', payload);
				} else {
					const extra = payload.data.extra && JSON.parse(payload.data.extra);

					if (extra) {
						const { successCount, failCount, recordCount } = extra;
						// Check if the import is success, failed or partial success
						if ((successCount === 0 && recordCount !== 0) || failCount === recordCount || !recordCount) {
							// success but failed
							showError({
								header: `${t(extra?.type ?? '')} ${t('notify.importNoSuccess.title')}`,
								link: 'seeDetails',
								message: t('notify.importNoSuccess.content'),
								linkAction: () => navigate(IMPORT_PATHS.importExport)
							});
						} else if (successCount > 0 && successCount === recordCount) {
							// success
							const pageUrl = IMPORT_PATHS[extra?.type] || IMPORT_PATHS.importExport;
							showSuccess({
								header: `${t(extra?.type ?? '')} ${payload.notification.title}`,
								message: payload.notification.body,
								link: 'seeDetails',
								linkAction: () => navigate(pageUrl)
							});
						} else if (successCount > 0 && failCount > 0) {
							// partial success
							showWarning({
								header: `${t(extra?.type ?? '')} ${t('partiallyImported')}`,
								message: t('notify.importPartially.content'),
								link: 'seeDetails',
								linkAction: () => navigate(IMPORT_PATHS.importExport)
							});
						}

						dispatch(updateDynamicListsInPage());
					} else {
						AppLogger.error('Notification: extra content not found on success ', payload);
					}
				}
			},
			// Failed import action notification
			[NOTIFICATION_TYPES.importNoSuccess]: (payload) => {
				if (!payload?.notification?.title) {
					showError();
					AppLogger.error('Import No Success Notification Missing Title: ', payload);
				} else {
					const extra = payload.data.extra && JSON.parse(payload.data.extra);
					showError({
						header: `${t(extra?.type ?? '')} ${payload.notification.title}`,
						message: payload.notification.body,
						link: 'seeDetails',
						linkAction: () => navigate(IMPORT_PATHS.importExport)
					});
					dispatch(updateDynamicListData(DYNAMICLISTS.IMPORT_LOGS));
				}
			},
			// Customer approved support access notification
			[NOTIFICATION_TYPES.approveSupportAccess]: (payload) => {
				showInfo({
					header: payload?.notification?.title,
					message: payload?.notification?.body
				});
				dispatch(updateDynamicListData(DYNAMICLISTS.CLIENT_USERS));
			},
			// Dynamiclist data export action notification
			[NOTIFICATION_TYPES.dataExport]: (payload) => {
				const {
					name = null,
					link = null,
					date = null,
					translationObject = null
				} = payload.data.extra && JSON.parse(payload.data.extra);

				const info = {
					header: payload?.notification?.title || t('notify.dataExport.title'),
					message: payload?.notification?.body || t('exportRequestTakenSuccess')
				};

				if (link) {
					info.link = 'download';
					info.linkAction = () => handleDownloadTemplate(name, link, date, translationObject);
				}

				if (name) {
					dispatch(setDynamicListExporting(false));
				} else {
					AppLogger.error('Notification: ', payload);
				}
				showInfo(info);
				dispatch(updateDynamicListData(DYNAMICLISTS.EXPORT_LOGS));
			},
			// Create shipment action success notification
			[NOTIFICATION_TYPES.shipmentCreated]: (payload) => {
				const {
					orderID = null,
					shipmentNumber = null,
					dcSettingsID = null
				} = payload.data.extra && JSON.parse(payload.data.extra);

				if (orderID) {
					dispatch(
						addShipmentResult({
							[orderID]: {
								shipmentNumber,
								dcSettingsID
							}
						})
					);
				}

				showSuccess({
					header: payload?.notification?.title ?? t('alert.operationCompletedSuccessfully'),
					message: payload?.notification?.body ?? ''
				});
			},
			// Create shipment action failed notification
			[NOTIFICATION_TYPES.shipmentFailed]: (payload) => {
				const {
					orderID = null,
					error = null,
					errorMsg = null
				} = payload.data.extra && JSON.parse(payload.data.extra);

				if (orderID) {
					dispatch(
						addShipmentResult({
							[orderID]: {
								error,
								errorMsg
							}
						})
					);
				}

				showError({
					header: payload?.notification?.title ?? t('alert.anErrorOccurred'),
					message: payload?.notification?.body ?? '',
					link: 'seeDetails',
					linkAction: () => navigate(mergePaths(PATHS.SYSTEM_LOGS, PATHS.SHIPMENT_ERROR_LOGS))
				});
			},
			// Order assigned to branch notification
			[NOTIFICATION_TYPES.orderAssigned]: (payload) => {
				const { otoID = null } = payload.data.extra && JSON.parse(payload.data.extra);

				const message = {
					header: payload?.notification?.title ?? t('alert.operationCompletedSuccessfully'),
					message: payload?.notification?.body ?? ''
				};

				const audio = playAudio();

				if (otoID) {
					const query = {
						modal: 'view-order-form',
						modalID: Date.now(),
						id: otoID
					};

					message.link = 'seeDetails';
					message.linkAction = () => {
						stopAudio(audio);
						navigate({
							pathname: PATHS.BR_PENDING_ORDERS,
							search: `?${createSearchParams(query)}`
						});
					};
					message.onClose = () => {
						stopAudio(audio);
					};
				} else {
					setTimeout(() => {
						stopAudio(audio);
					}, 10000);
				}

				showSuccess(message);
			},
			// Weight deduction note notification
			[NOTIFICATION_TYPES.weightDeductionNote]: (payload) => {
				const audio = playAudio();

				const message = {
					header: payload?.notification?.title ?? t('alert.reminder'),
					message: payload?.notification?.body ?? '',
					link: 'seeDetails',
					linkAction: () => {
						stopAudio(audio);
						navigate(PATHS.PAYMENTS);
					},
					onClose: () => {
						stopAudio(audio);
					}
				};

				showInfo(message);
			},
			// New order notification for the company users
			[NOTIFICATION_TYPES.newOrder]: (payload) => {
				const audio = playAudio();
				const extra = payload?.data?.extra && JSON.parse(payload.data.extra);

				const message = {
					header: payload?.notification?.title ?? t('alert.newOrder'),
					message: payload?.notification?.body ?? '',
					link: 'seeDetails',
					linkAction: () => {
						stopAudio(audio);
						if (extra?.otoLinkFlag) {
							navigate(mergePaths(PATHS.OTO_LINK, PATHS.ORDERS));
						} else {
							navigate(PATHS.SHIPMENTS);
						}
					},
					onClose: () => {
						stopAudio(audio);
					}
				};

				showInfo(message);
			},
			// Accepts any notification if there is no specified action for i
			[NOTIFICATION_TYPES.DEFAULT]: (payload) => {
				showInfo({
					header: payload?.notification?.title || t('alert.operationCompletedSuccessfully'),
					message: payload?.notification?.body || ''
				});
			}
		}),
		[
			handleDownloadTemplate,
			navigate,
			dispatch,
			showInfo,
			showError,
			showSuccess,
			showWarning,
			getLocaleDateWithTime,
			t
		]
	);

	const handleMessageListener = useCallback(
		(event) => {
			if (!event) return;

			dispatch(increaseNotificationCount());

			const notificationData = event.data;
			const notificationType = notificationData?.data?.type;

			if (!notificationType) {
				AppLogger.error('Unknown Notification Type: ', event);
			} else if (NOTIFICATION_ACTIONS[notificationType]) {
				NOTIFICATION_ACTIONS[notificationType](notificationData);
			} else {
				NOTIFICATION_ACTIONS.DEFAULT(notificationData);
			}
		},
		[dispatch, NOTIFICATION_ACTIONS]
	);

	useEffect(() => {
		navigator.serviceWorker.addEventListener('message', handleMessageListener);
		return () => {
			navigator.serviceWorker.removeEventListener('message', handleMessageListener);
		};
	}, [handleMessageListener]);
};

export default FirebaseNotifications;
